import { useId, useState } from "react";
import { useLinkGroups } from "@helm/core";
import { DEFAULT_TIMEFRAME } from "../chart/TimeframePicker";
import { DomLadder } from "../widgets/DomLadder";
import { OrderPad, type OrderPadMode } from "../widgets/OrderPad";
import { Tabs, tabPanelProps } from "../components/Tabs";
import { lazyComponent } from "../components/lazy";

// The chart is its own chunk (audit S-12); the DOM toggle and the order pad are in the entry chunk.
const ChartWidget = lazyComponent(() => import("../chart/ChartWidget").then((m) => m.ChartWidget), "chart");

const VIEWS = [
  ["chart", "Chart"],
  ["dom", "DOM"],
] as const;
type View = (typeof VIEWS)[number][0];

/**
 * Mobile Trade (Tradesea: chart-first, DOM toggle, bottom order pad; SPEC §17.7 v1). The pad is
 * the same disabled `OrderPad` as desktop — hold-to-confirm Buy/Sell replaces it with the order
 * core (Phase 9). Symbol follows the red link group (set from Markets).
 */
export function Trade(): React.JSX.Element {
  const id = useId();
  const symbol = useLinkGroups((s) => s.groups.red.symbol);
  const [view, setView] = useState<View>("chart");
  const [mode, setMode] = useState<OrderPadMode>("scalp");
  return (
    <div className="m-trade">
      <div className="m-trade-head">
        <span className="m-trade-symbol">{symbol ?? "—"}</span>
        <Tabs
          tabs={VIEWS}
          value={view}
          onChange={setView}
          label="Trade view"
          idBase={id}
          variant="segmented"
        />
      </div>
      <div className="m-trade-main m-card m-card-flush" {...tabPanelProps(id, view)}>
        {view === "chart" ? (
          <ChartWidget symbol={symbol} timeframe={DEFAULT_TIMEFRAME} />
        ) : (
          <DomLadder symbol={symbol} />
        )}
      </div>
      <div className="m-trade-pad m-card">
        <OrderPad symbol={symbol} mode={mode} onModeChange={setMode} />
      </div>
    </div>
  );
}
